import { BreakpointObserver } from "@angular/cdk/layout";
import { Component, Injectable } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BankAccountStatus, IBankAccountViewModel  } from "src/app/models/bank-account";
import { BankAccountService } from "src/app/services/http/bank-account.service";
import { GeneralSelectionFacade } from "src/app/store/general-selection/facade/general-selection.facade";
import { BankAccountEditCreateComponent } from "./bank-account-edit-create/bank-account-edit-create.component";
import { ClientEntity } from "src/app/models/clients";
import { MatDialog } from "@angular/material/dialog";
import { SnackBarService } from "src/app/services/snackbar/snackBar.service";
import { BankAccountFacade } from "src/app/store/bank-account/facade/bank-account.facade";
import { combineLatest } from "rxjs";

@Component({
    selector: 'app-bank-accounts',
    templateUrl: './bank-accounts.component.html',
    styleUrls: ['./bank-accounts.component.scss']
  })

@UntilDestroy()
@Injectable()
export class BankAccountComponent{

    public isMobile: boolean = false;
    public bankAccounts: IBankAccountViewModel [] = [];
    public bankAccountApprovalPopperText: string = 'Bank account approval text';

    private selectedEntities: ClientEntity[] = [];
    private bankAccountPermissionsMap = new Map<number, boolean>();

    constructor(
        private bankAccountService: BankAccountService,
        private generalSelectionFacade : GeneralSelectionFacade,
        private breakpointObserver: BreakpointObserver,
        private dialog: MatDialog,
        private snackBarService: SnackBarService,
        private bankAccountFacade: BankAccountFacade
    ){  
        this.subscribeToGeneralSelectionSelectedEntities();
        this.subscribeToMobileBreakPointObserver();
        this.subscribeToBankAccountsAndPermissions();
    }

    /**
     * Subscribes to the list of selected entities in the general selection state.
     */
    private subscribeToGeneralSelectionSelectedEntities(): void {
        this.generalSelectionFacade.getSelectedInvestorEntities()
        .pipe(untilDestroyed(this))
        .subscribe((entities) => {
            if(entities === null || entities.length === 0)
                return;

            this.selectedEntities = entities;
            this.bankAccountFacade.getCanApproveBankAccountPermissionsPerInvestor(entities);
            this.bankAccountFacade.getInvestorsBankAccountsForSelectedEntities();
        });
    }

    /**
     * Subscribes to the mobile breakpoint observer to determine when we have gone to mobile view.
     */
    private subscribeToMobileBreakPointObserver(): void {
        this.breakpointObserver
        .observe(["(max-width: 600px)"])
        .pipe(untilDestroyed(this))
        .subscribe(result => {
            this.isMobile = result.matches;
        });
    }

    /**
     * Subscribes to the bank account response observable and the bank account permissions approval observable.
     */
    public subscribeToBankAccountsAndPermissions() :void {
        combineLatest([
            this.bankAccountService.investorsBankAccountsResponse$,
            this.bankAccountFacade.bankAccountApprovalPermission$
        ])
        .subscribe(([bankAccountResponse, permissionsMapResponse]) => {
            if(bankAccountResponse.error || permissionsMapResponse.error){
                this.bankAccounts = [];
                this.snackBarService.showWarningSnackBar("Bank account data could not be loaded.");
                return;
            }

            if(bankAccountResponse.loading || permissionsMapResponse.loading)
                return;

            this.bankAccountPermissionsMap = permissionsMapResponse.data;
            this.bankAccounts = bankAccountResponse.data;

            if(this.bankAccounts.length === 0)
                return;

            this.bankAccounts.forEach(bankAccount => {
                bankAccount.investorEntityName = this.getEntityName(bankAccount.investorEntityId);
            });
        });

    }

    /**
     * Opens a modal to display further details about the chosen bank account.
     */
    public openBankAccountDialog(bankAccount: IBankAccountViewModel) : void {
        this.dialog.open(BankAccountEditCreateComponent,{
            data: {
              bankAccount: bankAccount,
              userCanApproveOrRejectBankAccount: this.userCanApproveOrRejectGivenBankAccount(bankAccount)
            }
        })
    }

    /**
     * Retrieves the investors name from the list of selected entities.
     */
    private getEntityName(entityId: number) : string {
        if(this.selectedEntities.length === 0)
            return;

        return this.selectedEntities.find(x => x.EntityId === entityId).Name;
    }

    /**
     * Sets the call to action text to show for each bank account.
     */
    public getCallToAction(bankAccount: IBankAccountViewModel) : string {

        if(this.userCanApproveOrRejectGivenBankAccount(bankAccount))
            return 'Approval Required'

        return 'View';
    }

    /**
     * Determines if the current user is able to approve or reject the given bank account.
     * For now the scenarios where a user can reject a bank account are the same as when they able to approve so we have one variable to represent both actions.
     */
    private userCanApproveOrRejectGivenBankAccount(bankAccount: IBankAccountViewModel): boolean {
        var canApprove = this.bankAccountFacade.userCanApproveOrRejectGivenBankAccount(bankAccount)
        if(!!!canApprove)
            return false;

        if(this.bankAccountPermissionsMap === null || this.bankAccountPermissionsMap === undefined)
            return false;

        //At this point we know user can approve the bank account but has not done so - check if they have permission.
        let hasPermission = this.bankAccountPermissionsMap.get(bankAccount.investorEntityId)
        if(hasPermission === null || hasPermission === undefined || !hasPermission)
            return false;

        return true;
    }

    /**
     * Determines whether to show the number of outstanding approvals based on the current status of the bank account and whether it has any approvals outstanding.
     */
    public showOutstandingApprovalsText(bankAccount: IBankAccountViewModel) : boolean {
        if(bankAccount.status !== BankAccountStatus.ApprovalRequired)
            return false;

        if(bankAccount.approvalsOutstanding < 1)
            return false;

        return true;
    }
}